<template>
  <div class="d-flex align-items-center">
    <b-button
      :disabled="adjustStock <= min || disabled"
      variant="outline-primary"
      size="sm"
      class="d-flex align-items-center justify-content-center"
      style="width: 38px; height: 38px; min-width: 38px; min-height: 38px"
      @click="onMinus"
    >
      <i class="fas fa-minus"></i>
    </b-button>
    <b-form-input
      inputmode="numeric"
      class="no-arrow text-center"
      style="max-width: 100px"
      :value="value"
      :max="max"
      :min="min"
      @input="onInputChange"
      @blur="onBlur($event)"
      @paste="onPaste($event)"
      :disabled="disabled"
      :class="{
        'is-invalid': invalid,
      }"
      @keydown="allowOnlyTwoDecimalNumber($event)"
    ></b-form-input>
    <b-button
      :disabled="adjustStock >= max || disabled"
      variant="outline-primary"
      size="sm"
      class="d-flex align-items-center justify-content-center"
      style="width: 38px; height: 38px; min-width: 38px; min-height: 38px"
      @click="onPlus"
    >
      <i class="fas fa-plus"></i>
    </b-button>
  </div>
</template>

<script>
import "@/theme/modal/ProductAndStock/Product.scss";
import BigNumber from "bignumber.js";
import { validateTwoDecimal } from "@/utils/numberValidation";

export default {
  name: "InputNumberWithButtonField",
  components: {},
  props: {
    adjustStock: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      value: new BigNumber(this.adjustStock).toNumber(),
    };
  },
  validations: {},
  computed: {},
  methods: {
    onInputChange(value) {
      this.value = value;
      const raw = (value ?? "").toString().trim();
      if (raw === "") return;

      const bn = new BigNumber(this.value);
      if (!bn.isFinite()) return;

      this.onChange(bn.toFixed(2));
    },
    onMinus() {
      if (this.value > this.min) {
        const result = new BigNumber(this.value).minus(1);
        if (!validateTwoDecimal(result)) {
          this.onChange(result.toFixed(2));
        } else {
          this.onChange(result.toNumber());
        }
      }
    },
    onPlus() {
      if (this.value < this.max) {
        const result = new BigNumber(this.value).plus(1);
        if (!validateTwoDecimal(result)) {
          this.onChange(result.toFixed(2));
        } else {
          this.onChange(result.toNumber());
        }
      }
    },
    onBlur() {
      let raw = (this.value ?? "").toString().trim();

      if (raw === "") raw = "0";

      //  -0, -0.0, 0.0, 00.00, 000, 000.000
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);
      if (isZeroLike) {
        this.value = "0";
        this.onChange(new BigNumber(0));
        return;
      }

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) {
        this.value = "0";
        this.onChange(new BigNumber(0));
        return;
      }

      if (this.value < this.min) {
        this.value = new BigNumber(this.min).toNumber();
      } else if (this.value > this.max) {
        this.value = new BigNumber(this.max).toNumber();
      } else if (!validateTwoDecimal(this.value)) {
        this.value = new BigNumber(this.value);
      }
      this.onChange(new BigNumber(this.value));
    },
    allowOnlyTwoDecimalNumber(e) {
      if (e.ctrlKey || e.metaKey) {
        const k = (e.key || "").toLowerCase();
        const allowed = ["a", "c", "v", "x", "z", "y"];
        if (allowed.includes(k)) return;
      }
      if (e.shiftKey && e.key === "Insert") return;

      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];

      if (allowedKeys.includes(e.key)) return;

      const input = e.target;
      const value = input.value || "";
      const { selectionStart, selectionEnd } = input;

      // Minus: only at the very start
      if (e.key === "-") {
        if (selectionStart !== 0) {
          e.preventDefault();
        }
        return;
      }

      // One decimal point only
      if (e.key === ".") {
        if (value.includes(".")) {
          e.preventDefault();
        }
        return;
      }

      // Digits 0–9: allow only if result would have at most 2 decimal places
      if (/^\d$/.test(e.key)) {
        const newValue =
          value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
        const parts = newValue.split(".");
        if (parts.length === 2 && parts[1].length > 2) {
          e.preventDefault();
        }
        return;
      }
      e.preventDefault();
    },
    onPaste(e) {
      const raw = (
        (e.clipboardData || window.clipboardData)?.getData("text") || ""
      )
        .trim()
        .replace(/,/g, "");

      if (
        !raw ||
        raw === "-" ||
        raw === "." ||
        !/^-?\d*(?:\.\d*)?$/.test(raw)
      ) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      let bn = new BigNumber(raw);
      if (!bn.isFinite()) return;

      const minBN = new BigNumber(this.min ?? 0);
      const maxBN = new BigNumber(this.max ?? 0);

      if (bn.isLessThan(minBN)) bn = minBN;
      if (bn.isGreaterThan(maxBN)) bn = maxBN;

      //ROUND_HALF_UP
      bn = bn.decimalPlaces(2, BigNumber.ROUND_DOWN);

      this.value = bn.isZero() ? "0" : bn.toString();
      this.onChange(bn);
    },
  },
  watch: {
    adjustStock(newVal) {
      const result = new BigNumber(newVal);
      if (!validateTwoDecimal(result)) {
        this.value = result.toFixed(2);
      } else {
        this.value = result.toNumber();
      }
    },
  },
};
</script>

<style scoped>
.no-arrow::-webkit-inner-spin-button,
.no-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-arrow {
  -moz-appearance: textfield;
}

button:disabled {
  background-color: white;
  border-color: #ebebeb;
  color: #ebebeb;
  cursor: not-allowed;
}

.was-validated .form-control:invalid,
.form-control.is-invalid {
  background-image: none !important;
  padding: 0 !important;
}
</style>
